// src/utils/editorStyles.js

/**
 * Global editor styles that can be imported in the app's global CSS
 * These styles handle all the TipTap editor, hashtags, placeholders,
 * and other UI styling requirements
 */
export const globalEditorStyles = `
/* TipTap placeholder styling */
.ProseMirror p.is-editor-empty:first-of-type::before {
    content: attr(data-placeholder);
    float: left;
    color: #adb5bd;
    pointer-events: none;
    height: 0;
}

/* Handle placeholders for different block types */
.ProseMirror h1.is-empty:first-of-type::before,
.ProseMirror h2.is-empty:first-of-type::before,
.ProseMirror h3.is-empty:first-of-type::before,
.ProseMirror li.is-empty:first-of-type::before,
.ProseMirror pre.is-empty:first-of-type::before,
.ProseMirror blockquote.is-empty:first-of-type::before {
    content: attr(data-placeholder);
    float: left;
    color: #adb5bd;
    pointer-events: none;
    height: 0;
}

/* Style for hashtags */
.hashtag {
    color: #3b82f6;
    font-weight: 500;
    cursor: pointer;
    background-color: rgba(59, 130, 246, 0.1);
    border-radius: 4px;
    padding: 0 4px;
    white-space: nowrap;
}

.hashtag:hover {
    background-color: rgba(59, 130, 246, 0.2);
}

/* Hashtag suggestion popup styles */
.hashtag-suggestions {
    background: white;
    border-radius: 6px;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.15);
    overflow: hidden;
    padding: 8px 0;
    width: 220px;
    max-height: 200px;
    overflow-y: auto;
    z-index: 100;
}

.hashtag-item {
    background: transparent;
    border: none;
    border-radius: 0;
    color: #333;
    cursor: pointer;
    display: flex;
    justify-content: flex-start;
    align-items: center;
    font-size: 14px;
    padding: 8px 12px;
    text-align: left;
    width: 100%;
    transition: background-color 0.1s ease;
}

.hashtag-item:hover {
    background-color: #f3f4f6;
}

.hashtag-item.selected {
    background-color: #eff6ff;
}

.hashtag-item.create-new {
    border-top: 1px solid #e5e7eb;
    margin-top: 4px;
    padding-top: 8px;
}

.hashtag-item.create-new span:first-child {
    color: #6b7280;
    font-size: 12px;
    margin-right: 6px;
}

.hashtag-item .tag-name {
    color: #3b82f6;
    font-weight: 500;
}

.hashtag-no-results {
    color: #9ca3af;
    padding: 8px 12px;
    font-size: 13px;
    font-style: italic;
}

/* Reset margins for TipTap content */
.ProseMirror {
    margin: 0;
    padding: 6px; /* Add some basic padding to all ProseMirror content */
}

.ProseMirror p {
    margin: 2px 0;
}

.editor-paragraph {
    margin: 0;
    padding: 2px 4px; /* Add padding to paragraphs */
}

.ProseMirror h1,
.ProseMirror h2,
.ProseMirror h3 {
    margin: 0;
    padding: 2px 4px; /* Add padding to headings */
}

.ProseMirror ul,
.ProseMirror ol {
    margin: 0;
    padding-left: 1.2rem;
}

.ProseMirror li {
    margin: 0;
}

/* Bubble menu styling */
.bubble-menu {
    display: flex;
    background-color: #ffffff;
    border: 1px solid #e2e8f0;
    border-radius: 0.375rem;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
    padding: 0.25rem;
    visibility: hidden; /* TipTap will handle showing this */
}

.bubble-menu button {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 2rem;
    height: 2rem;
    margin: 0 0.125rem;
    border: none;
    background: transparent;
    border-radius: 0.25rem;
    cursor: pointer;
}

.bubble-menu button:hover {
    background-color: #f3f4f6;
}

.bubble-menu button.active {
    color: #3b82f6;
    background-color: #eff6ff;
}

.bubble-menu button svg {
    width: 1.25rem;
    height: 1.25rem;
}

// Updated TaskList Styling in editorStyles.js

/* TaskList styling with improved alignment */
.ProseMirror ul[data-type="taskList"] {
  list-style: none;
  padding: 0;
  margin: 0.5rem 0;
}

.ProseMirror ul[data-type="taskList"] li {
  display: flex;
  align-items: center; /* Changed from flex-start to center */
  margin-bottom: 0.5rem;
  min-height: 1.5rem;
  position: relative;
}

.ProseMirror ul[data-type="taskList"] li > label {
  flex: 0 0 auto;
  margin-right: 0.75rem;
  display: flex;
  align-items: center;
  justify-content: center;
  user-select: none;
}

.ProseMirror ul[data-type="taskList"] li > div {
  flex: 1 1 auto;
  display: flex;
  align-items: center;
  min-height: 1.5rem;
}

.ProseMirror ul[data-type="taskList"] li > div > p {
  margin: 0;
  line-height: 1.5;
}

/* Custom checkbox styling */
.ProseMirror ul[data-type="taskList"] li > label input[type="checkbox"] {
  appearance: none;
  -webkit-appearance: none;
  -moz-appearance: none;
  width: 1.25rem;
  height: 1.25rem;
  border: 1px solid #d1d5db;
  border-radius: 0.25rem;
  margin: 0;
  padding: 0;
  cursor: pointer;
  position: relative;
  background-color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* Create the checkmark using pseudo-elements with better centering */
.ProseMirror ul[data-type="taskList"] li > label input[type="checkbox"]:checked {
  background-color: #5cb85c; /* Green background color */
  border-color: #5cb85c;
}

.ProseMirror ul[data-type="taskList"] li > label input[type="checkbox"]:checked::after {
  content: "";
  position: absolute;
  left: 50%;
  top: 40%; /* Slightly adjusted from 50% to account for visual center */
  width: 0.375rem;
  height: 0.625rem;
  border: solid white;
  border-width: 0 2px 2px 0;
  transform: translate(-50%, -50%) rotate(45deg);
}

.ProseMirror ul[data-type="taskList"] li > label input[type="checkbox"]:focus {
  outline: none;
  box-shadow: 0 0 0 2px rgba(92, 184, 92, 0.3);
}

/* Unchecked hover state */
.ProseMirror ul[data-type="taskList"] li > label input[type="checkbox"]:hover:not(:checked) {
  background-color: #f8f9fa;
  border-color: #b0b0b0;
}

/* Checked hover state */
.ProseMirror ul[data-type="taskList"] li > label input[type="checkbox"]:hover:checked {
  background-color: #4cae4c; /* Slightly darker green on hover */
}

/* Optional: Completed task text styling */
.ProseMirror ul[data-type="taskList"] li[data-checked="true"] > div p {
  color: #6b7280;
  text-decoration: line-through;
}

/* Unchecked hover state */
.ProseMirror ul[data-type="taskList"] li > label input[type="checkbox"]:hover:not(:checked) {
    background-color: #f8f9fa;
    border-color: #b0b0b0;
}

/* Checked hover state */
.ProseMirror ul[data-type="taskList"] li > label input[type="checkbox"]:hover:checked {
    background-color: #4cae4c; /* Slightly darker green on hover */
}

/* Completed task text styling (optional) */
.ProseMirror ul[data-type="taskList"] li[data-checked="true"] > div p {
    color: #6b7280;
    text-decoration: line-through;
}

/* Selected block styling */
.selected-block .editor-content {
    background-color: #f3f4f6;
    padding: 12px 16px; /* Increased padding when selected */
    border-radius: 5px;
    transition: padding 0.2s ease;
}

/* Main editor content */
.editor-content {
    flex: 1;
    padding: 6px 10px;
    min-height: 24px;
    outline: none;
    background-color: #fcf5e5;
    border-radius: 5px;
    font-size: 18px;
}

/* Editor wrapper */
.editor-wrapper {
    position: relative;
    width: 100%;
}

/* Link styling */
.ProseMirror a {
    color: #3b82f6;
    text-decoration: underline;
    cursor: pointer;
}

.ProseMirror a:hover {
    text-decoration: none;
}

/* Code block styling */
/* Code block styling with dark blue background */
.ProseMirror pre {
  background-color: #1e293b; /* Dark blue background color */
  color: #e2e8f0; /* Light text color for readability */
  border-radius: 6px;
  padding: 16px;
  font-family: 'Fira Code', 'Menlo', 'Monaco', 'Courier New', monospace;
  font-size: 0.9rem;
  line-height: 1.5;
  overflow-x: auto;
  margin: 1rem 0;
  position: relative;
}

/* Fix for the grey background on individual lines */
.ProseMirror pre code {
  background-color: transparent !important; /* Override any background on individual code lines */
  color: inherit;
  padding: 0;
  font-family: inherit;
  font-size: inherit;
  border-radius: 0;
  display: block;
  width: 100%;
}

/* Make sure individual code spans don't have backgrounds either */
.ProseMirror pre code span {
  background-color: transparent !important;
}

/* Style for inline code */
.ProseMirror code:not(pre code) {
  background-color: rgba(226, 232, 240, 0.2);
  border-radius: 4px;
  padding: 2px 4px;
  font-family: 'Fira Code', 'Menlo', 'Monaco', 'Courier New', monospace;
  font-size: 0.9em;
  color: #f97316; /* Orangish color for inline code */
}

/* Language indicator in top-right corner */
.ProseMirror pre::before {
  content: attr(data-language);
  position: absolute;
  top: 4px;
  right: 8px;
  font-size: 0.75rem;
  color: #94a3b8;
  text-transform: uppercase;
  border-radius: 3px;
  padding: 2px 6px;
  background-color: rgba(0, 0, 0, 0.3);
}

/* Highlight.js syntax colors for dark theme - override the default colors */
.hljs-keyword,
.hljs-built_in,
.hljs-type {
  color: #93c5fd !important; /* Light blue */
}

.hljs-string,
.hljs-regexp,
.hljs-literal {
  color: #86efac !important; /* Light green */
}

.hljs-number {
  color: #fdba74 !important; /* Light orange */
}

.hljs-comment {
  color: #94a3b8 !important; /* Slate gray */
  font-style: italic;
}

.hljs-function,
.hljs-title.function_ {
  color: #c4b5fd !important; /* Light purple */
}

.hljs-property,
.hljs-attr,
.hljs-variable {
  color: #e2e8f0 !important; /* Light gray */
}

.hljs-title,
.hljs-class,
.hljs-title.class_ {
  color: #fcd34d !important; /* Light yellow */
}

/* Ensure code blocks have full width */
.ProseMirror pre .code-block {
  width: 100%;
  background-color: transparent !important;
}

/* Remove any scrollbars unless code overflows */
.ProseMirror pre::-webkit-scrollbar {
  height: 8px;
}

.ProseMirror pre::-webkit-scrollbar-track {
  background: rgba(0, 0, 0, 0.1);
  border-radius: 8px;
}

.ProseMirror pre::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.2);
  border-radius: 8px;
}

.ProseMirror pre::-webkit-scrollbar-thumb:hover {
  background: rgba(255, 255, 255, 0.3);
}

/* Blockquote styling */
.ProseMirror blockquote {
    border-left: 3px solid #e5e7eb;
    padding-left: 1rem;
    margin-left: 0;
    color: #6b7280;
}

/* Heading styles */
.ProseMirror h1 {
    font-size: 1.8rem;
    font-weight: 600;
}

.ProseMirror h2 {
    font-size: 1.5rem;
    font-weight: 600;
}

.ProseMirror h3 {
    font-size: 1.3rem;
    font-weight: 600;
}

/* Mobile responsive adjustments */
@media (max-width: 640px) {
    .editor-content {
        font-size: 16px;
        padding: 4px 8px;
    }

    .bubble-menu {
        padding: 0.15rem;
    }

    .bubble-menu button {
        width: 1.75rem;
        height: 1.75rem;
    }

    .bubble-menu button svg {
        width: 1rem;
        height: 1rem;
    }
}

/* Ensure editor takes full width of container */
.ProseMirror {
    width: 100%;
}
`;

/**
 * Helper function to apply global editor styles to the document
 */
export function applyEditorStyles() {
  const styleElement = document.createElement("style");
  styleElement.innerHTML = globalEditorStyles;
  document.head.appendChild(styleElement);
}

/**
 * Method to get only specific styles for a particular element
 * @param {string} selector - CSS selector to extract styles for
 * @returns {string} CSS styles for the specific selector
 */
export function getStylesForSelector(selector) {
  const lines = globalEditorStyles.split("\n");
  let collecting = false;
  let bracketCount = 0;
  let result = "";

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];

    if (line.includes(selector) && line.includes("{")) {
      collecting = true;
      bracketCount = 1;
      result += line + "\n";
      continue;
    }

    if (collecting) {
      result += line + "\n";

      if (line.includes("{")) bracketCount++;
      if (line.includes("}")) bracketCount--;

      if (bracketCount === 0) {
        collecting = false;
        break;
      }
    }
  }

  return result;
}
